import { Box, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { QRCode } from 'react-qr-code';
import type { MagicLinkPurpose } from '@bower/shared';
import { useMagicLink } from '../../hooks/auth/useMagicLink';

function useCountdown(expiresAt: string | null): number | null {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!expiresAt) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const diff = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
      setSecondsLeft(Math.max(0, diff));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return secondsLeft;
}

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

interface MobileCaptureQRProps {
  /**
   * Where the phone lands after redeeming. The purpose travels WITH the token
   * (it's chosen at generate time), so this is the whole of the difference
   * between the capture QR and the live QR.
   */
  readonly purpose?: MagicLinkPurpose;
  /** Caption under the QR. Defaults to the generic capture wording. */
  readonly caption?: string;
  /**
   * QR module size in px. Default 116 suits the checklist panel's narrow
   * column. A magic-link payload is ~105 bytes (64 hex chars of token), which
   * is QR v5 — 37×37 modules — so at 116px each module is only ~3.1 CSS px.
   * Surfaces with room to spare should pass more: scannability is the entire
   * job, and this is the one control that trades nothing for it.
   */
  readonly size?: number;
}

export function MobileCaptureQR({
  purpose = 'mobile_capture',
  caption = "Scan to open on your phone: you'll be signed in automatically",
  size = 116,
}: MobileCaptureQRProps) {
  const { qrUrl, expiresAt, isLoading, isError } = useMagicLink(purpose);
  const secondsLeft = useCountdown(expiresAt);
  const isExpired = secondsLeft !== null && secondsLeft <= 0;
  const isUrgent = secondsLeft !== null && secondsLeft < 120;

  if (isError) {
    return (
      // Named and announced: two QRs can be on screen, so an anonymous
      // failure leaves the user unable to tell which one broke — and with no
      // role it isn't announced at all.
      <Text role="alert" fontSize="xs" color="text.muted" textAlign="center" py={2}>
        Couldn&apos;t generate this QR code ({caption}): refresh the page to try again.
      </Text>
    );
  }

  return (
    <VStack spacing={2} align="center" py={3}>
      {isLoading || !qrUrl ? (
        // Sized from the same `size` as the loaded QR (+ the 2×p2 padding and
        // 2×1px border below), so the swap doesn't shift the layout.
        <Skeleton w={`${size + 18}px`} h={`${size + 18}px`} borderRadius="md" />
      ) : (
        // bg="white" is the Chakra colour alias (not a semantic token); it
        // always resolves to #FFFFFF and does not flip in dark mode — correct
        // for QR codes which require a white quiet zone to scan reliably.
        <Box
          // react-qr-code doesn't forward aria onto its <svg>, so without this
          // the code is an unnamed graphic. Named from the caption so two QRs
          // on one screen (capture + live) are distinguishable rather than
          // being two identical anonymous images. Mirrors MobileAccessQR.
          role="img"
          aria-label={`QR code: ${caption}`}
          bg="white"
          p={2}
          borderRadius="md"
          border="1px"
          borderColor="border.default"
          opacity={isExpired ? 0.4 : 1}
          transition="opacity 0.2s"
        >
          <QRCode value={qrUrl} size={size} />
        </Box>
      )}

      <Text fontSize="xs" color="text.muted" textAlign="center" maxW="200px">
        {caption}
      </Text>

      {/* Two nodes, deliberately. Only the expiry is worth interrupting for —
          a polite live region on a 1 Hz countdown announces every tick, and
          with two QRs mounted that is two announcements per second. But a node
          that BECOMES a live region in the same render as its content is not
          reliably announced by NVDA/JAWS/VoiceOver, so toggling `aria-live`
          would trade "announces constantly" for "announces never". The live
          region is therefore always mounted and starts empty; the ticking
          countdown is a separate, non-live node. */}
      {!isLoading && (
        <Text fontSize="xs" color="text.error" aria-live="polite">
          {isExpired ? 'Code expired: refresh the page for a new one' : ''}
        </Text>
      )}
      {secondsLeft !== null && !isLoading && !isExpired && (
        <Text fontSize="xs" color={isUrgent ? 'text.warning' : 'text.muted'}>
          {`Expires in ${formatCountdown(secondsLeft)}`}
        </Text>
      )}
    </VStack>
  );
}
